/**
 * UserPickerPanel — the data-connected wrapper around UserSearchPanel.
 *
 * Encapsulates the server-driven wiring (backend search/filter query, offset
 * pagination, UserEntity → PanelUser adaptation, photo thumbnails) so any place
 * that needs to pick a user gets the exact same list UI as the main
 * /user-profile "All Users" panel. Callers only deal in UserEntity.
 */

import { useState, useMemo, useCallback } from 'react';
import {
  UserSearchPanel,
  UserPanelFilters,
} from 'src/ui/patterns/UserSearchPanel';
import { defaultUserFilterGroups } from 'src/ui/patterns/userFiltersConfig';
import { toPanelUser } from 'src/ui/patterns/userAdapter';
import { buildUsersQuery } from 'src/ui/patterns/userFilterMapping';
import { useFetchManyUsersQuery, UserEntity } from 'src/store/userApi';

const DEFAULT_PAGE_SIZE = 100;

const EMPTY_FILTERS: UserPanelFilters = {
  search: '',
  selections: {},
  ageMin: '',
  ageMax: '',
  includeDeleted: false,
};

export interface UserPickerPanelProps {
  /** Currently selected user id (highlighted row) in single-select mode. */
  selectedUserId?: string;
  /** Multi-select mode: renders a checkbox per row; `onSelect` toggles. */
  multiSelect?: boolean;
  /** Selected user ids for multi-select highlighting. */
  selectedUserIds?: string[];
  /** Fires with the full UserEntity when a row is picked/toggled. */
  onSelect: (user: UserEntity | null) => void;
  /** User ids to hide from the list (e.g. the user being matched). */
  excludeUserIds?: string[];
  /** Panel title; defaults to "Users". */
  title?: string;
  /** Explicit panel height. */
  height?: number | string;
  /** Show the "Include deleted" toggle. */
  enableDeleted?: boolean;
  /** Rows per page (server caps at 100). */
  pageSize?: number;
  /** Skip fetching (e.g. while a containing modal is closed). */
  skip?: boolean;
}

export function UserPickerPanel({
  selectedUserId,
  multiSelect,
  selectedUserIds,
  onSelect,
  excludeUserIds,
  title = 'Users',
  height,
  enableDeleted = false,
  pageSize = DEFAULT_PAGE_SIZE,
  skip,
}: UserPickerPanelProps) {
  const [filters, setFilters] = useState<UserPanelFilters>(EMPTY_FILTERS);
  const [offset, setOffset] = useState(0);

  const usersQuery = useMemo(
    () => buildUsersQuery(filters, offset, pageSize),
    [filters, offset, pageSize]
  );

  const { data, isFetching } = useFetchManyUsersQuery(usersQuery, { skip });

  const excludeSet = useMemo(
    () => new Set(excludeUserIds ?? []),
    [excludeUserIds]
  );

  const rawUsers = data?.data ?? [];
  const visible = rawUsers.filter((u) => !excludeSet.has(u.id));
  const panelUsers = visible.map(toPanelUser);

  const userById = useMemo(() => {
    const map: Record<string, UserEntity> = {};
    visible.forEach((u) => {
      map[u.id] = u;
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const totalUsers = data?.meta?.total;
  const hasMore = data?.meta?.hasMore ?? false;

  const handleFiltersChange = useCallback((next: UserPanelFilters) => {
    setFilters(next);
    setOffset(0);
  }, []);

  return (
    <UserSearchPanel
      users={panelUsers}
      totalCount={totalUsers}
      selectedId={selectedUserId}
      multiSelect={multiSelect}
      selectedIds={selectedUserIds}
      title={title}
      height={height}
      enableDeleted={enableDeleted}
      filterGroups={defaultUserFilterGroups}
      loading={isFetching}
      onFiltersChange={handleFiltersChange}
      pagination={{
        offset,
        limit: pageSize,
        hasMore,
        total: totalUsers,
        onOffsetChange: setOffset,
      }}
      onSelect={(id) => onSelect(userById[id] ?? null)}
    />
  );
}

export default UserPickerPanel;
